import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { lazy, useEffect } from "react";
import { z } from "zod";

import { useViewerHasPermission } from "@/auth/shared";
import {
  Disclosure,
  DisclosureGroup,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { title } from "@/components/base/primitives";
import { RichTextDisplay } from "@/components/base/rich-text-editor/display";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

const CreateFaqForm = lazy(async () => {
  const mod = await import("@/components/faqs/create-faq-form");
  return { default: mod.CreateFaqForm };
});

const DeleteFaqForm = lazy(async () => {
  const mod = await import("@/components/faqs/delete-faq-form");
  return { default: mod.DeleteFaqForm };
});

const ReorderFaqsForm = lazy(async () => {
  const mod = await import("@/components/faqs/reorder-faqs-form");
  return { default: mod.ReorderFaqsForm };
});

const UpdateFaqForm = lazy(async () => {
  const mod = await import("@/components/faqs/update-faq-form");
  return { default: mod.UpdateFaqForm };
});

const titles: { [key: string]: string } = {
  tournaments: "Tournament",
  waitlist: "Waitlist",
  parents: "Parent's",
};

const faqSearchSchema = z.object({
  faq: z.string().optional(),
});

export const Route = createFileRoute("/faqs/{-$subject}")({
  ssr: "data-only",
  validateSearch: faqSearchSchema,
  loader: async ({
    params: { subject = null },
    context: { queryClient, trpc },
  }) => {
    if (subject !== null && !Object.hasOwn(titles, subject)) {
      throw notFound();
    }

    return await queryClient.ensureQueryData(
      trpc.faqs.list.queryOptions({ key: subject }),
    );
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { subject } = Route.useParams();
  const { faq: expandFaq } = Route.useSearch();
  const trpc = useTRPC();

  const { data: faqs } = useSuspenseQuery(
    trpc.faqs.list.queryOptions({ key: subject }),
  );

  const canCreate = useViewerHasPermission({
    faqs: ["create"],
  });

  const canUpdate = useViewerHasPermission({
    faqs: ["update"],
  });

  const expandedFaq = faqs.find(
    (f) => expandFaq && f.question.toLowerCase() === expandFaq.toLowerCase(),
  );

  useEffect(() => {
    if (!expandedFaq) return;
    const el = document.getElementById(`faq-${expandedFaq.id}`);
    if (el) {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    }
  }, [expandedFaq]);

  return (
    <DefaultLayout>
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-12 flex items-center justify-between">
          <h1 className={title()}>
            {subject && titles[subject] ? (
              <>{titles[subject]} FAQs</>
            ) : (
              <>Frequently Asked Questions</>
            )}
          </h1>

          <div className="flex flex-row gap-2">
            {canUpdate && <ReorderFaqsForm groupKey={subject} />}
            {canCreate && <CreateFaqForm groupKey={subject} />}
          </div>
        </div>

        {faqs && faqs.length === 0 && <p>No FAQs yet...</p>}

        {Boolean(faqs?.length) && (
          <DisclosureGroup
            defaultExpandedKeys={
              expandedFaq ? [`faq-${expandedFaq.id}`] : undefined
            }
          >
            {faqs.map(({ id, question, answer }) => (
              <Disclosure key={id} id={`faq-${id}`}>
                <DisclosureHeader
                  color="alt"
                  contentClassName="flex-1 flex flex-row justify-between items-center"
                >
                  <span>{question}</span>

                  {canUpdate && (
                    <span className="flex flex-row items-center space-x-2">
                      <UpdateFaqForm id={id} groupKey={subject} />
                      <DeleteFaqForm id={id} question={question} />
                    </span>
                  )}
                </DisclosureHeader>
                <DisclosurePanel color="alt">
                  <RichTextDisplay
                    name="faq-answer"
                    content={
                      typeof answer === "string" ? JSON.parse(answer) : answer
                    }
                  />
                </DisclosurePanel>
              </Disclosure>
            ))}
          </DisclosureGroup>
        )}
      </div>
    </DefaultLayout>
  );
}
